/**
 * rig → 模型姿态的唯一施加入口（P0-8 修复：恢复/打开后姿势不重现）。
 *
 * 背景：姿势数据存在 object.rig 里，但"把 rig 合成并拧到模型上"这件事原先只在
 * 用户手动操作时由页面命令式触发；刷新/打开文件后模型重新加载成静止姿态，
 * 没有代码回读 rig 重新摆 → 表现为"姿势失效、回到初始"。
 *
 * 定案：模型 = f(rig)。SceneObject 加载完成时调用本函数按 obj.rig 摆好；
 * 页面交互（滑杆/预设/逐骨）也走同一函数，单一来源不再各写一份。
 */
import { composeBones, type CharacterRig } from '../../model/pose';
import { applyBoneMap, type RestSnapshot } from './poseApply';
import { loadPoseBones } from './poseCatalog';
import { restBoneMap } from './poseEdit';
import type { Object3D } from 'three';

/** rig 是否含任何姿态意图（预设/关节微调/单骨覆盖）；空则不必施加，省一次异步拉取 */
export function rigHasPose(rig: CharacterRig | undefined): boolean {
  if (rig === undefined) {
    return false;
  }
  return (
    rig.posePresetId !== null ||
    Object.keys(rig.controls).length > 0 ||
    Object.keys(rig.boneOverrides).length > 0
  );
}

/** 合成 rig（预设或全骨零位 ⊕ controls ⊕ overrides）并施加到模型。返回命中骨数。 */
export async function applyRigToModel(
  root: Object3D,
  rest: RestSnapshot,
  rig: CharacterRig,
): Promise<number> {
  const base = rig.posePresetId !== null ? await loadPoseBones(rig.posePresetId) : restBoneMap();
  const bones = composeBones(base, rig.controls, rig.boneOverrides);
  return applyBoneMap(root, bones, rest).applied;
}
